import './styles.css';
import {choosePileToys,PILE_LIMIT} from './collection-pile.js';
import { UI } from './ui.js';
import { ToyStore, decodeTransfer } from './state.js';
import { Stage } from './stage.js';
import { toySound } from './toy-sound.js';
import { Navigation } from './navigation.js';
import { Metrics, PlayTimer, beaconSender } from './metrics.js';

let storage;
try { storage=window.localStorage; } catch { storage={getItem:()=>null,setItem:()=>{throw new Error('Storage unavailable');}}; }
const params=new URLSearchParams(window.location.search);
const localOrigin=['localhost','127.0.0.1'].includes(window.location.hostname);
const standalone=matchMedia('(display-mode: standalone)').matches||navigator.standalone===true;
const store=new ToyStore(storage,{locale:navigator.language.startsWith('ja')?'ja':'en',reducedMotion:matchMedia('(prefers-reduced-motion: reduce)').matches});
// Usage counts stay off during local development unless ?metrics=1 is given.
const metrics=new Metrics({storage,send:beaconSender(),enabled:!localOrigin||params.has('metrics'),
  context:{locale:store.state.locale,pwa:standalone?1:0,mobile:matchMedia('(pointer: coarse)').matches?1:0,source:params.get('source')??''}});
const playTimer=new PlayTimer(metrics);
const touched=new Set();
let stage,installPrompt=null,persistRequested=false;
const ui=new UI(document.querySelector('#app'),action);
const navigation=new Navigation({window,store,render,getScroll:()=>ui.getScroll(),restoreScroll:value=>ui.restoreScroll(value)});
function reshufflePile(){store.state.pileIds=choosePileToys(store.state.collection).map(toy=>toy.id);store.state.pileRevision=(store.state.pileRevision??0)+1;}
function currentKind(){const s=store.state;return s.previewKind??s.collection.find(toy=>toy.id===s.selectedId)?.kind??null;}
function trackPlay(){playTimer.update(store.state.screen==='play'&&!document.hidden,currentKind());}
function render(){
  if(store.state.screen==='pile'&&(!store.state.pileIds||store.state.pileIds.length!==Math.min(PILE_LIMIT,store.state.collection.length)||store.state.pileIds.some(id=>!store.state.collection.some(toy=>toy.id===id))))reshufflePile();
  ui.render(store.state);stage?.sync(store.state);trackPlay();
}
function status(value){
  if(value==='loading'&&store.state.status==='save-error')return;
  if(value===null&&store.state.status==='save-error')return;
  store.state.status=value;ui.render(store.state);
}
// Ask once, after the first acquired toy, so the browser is less likely to
// evict the collection under storage pressure.
function requestPersistentStorage(){
  if(persistRequested)return;persistRequested=true;
  navigator.storage?.persist?.().catch(()=>{});
}
function action(name,payload){
  if(name==='retry'){
    if(store.state.status==='model-error'&&stage){status(null);if(store.state.screen==='pile')stage.pileKey=null;stage.sync(store.state);}
    else window.location.reload();
    return;
  }
  if(navigation.navigate(name,payload))return;
  if(name==='pile-mode')store.state.pileMode=payload;
  else if(name==='pile-camera-reset')stage?.pileView?.resetCamera();
  else if(name==='pile-zoom')stage?.pileView?.zoom(payload);
  else if(name==='pile-squeeze')stage?.pileView?.squeeze(payload);
  else if(name==='pile-shuffle')reshufflePile();
  else if(name==='draw'){if(store.draw())metrics.track('draw');}
  else if(name==='open'){const toy=store.open();if(toy){metrics.track('open',{kind:toy.kind,rare:toy.variant?1:0});requestPersistentStorage();}}
  else if(name==='locale'){store.state.locale=payload;store.persist();metrics.context.locale=payload;}
  else if(name==='motion'){store.state.reducedMotion=!store.state.reducedMotion;store.persist();}
  else if(name==='mode')store.state.mode=payload;
  else if(name==='reset'){store.reset();stage?.reset();}
  else if(name==='squeeze')stage?.squeeze();
  else if(name==='powder'){if(stage?.extras?.dust()){stage.dirty=true;}}
  else if(name==='flip')stage?.flip();
  // Sound is off until chosen; the press itself lets audio start. The saved
  // "muted" setting keeps the choice.
  else if(name==='sound'){store.state.muted=!store.state.muted;store.persist();toySound.setEnabled(!store.state.muted);}
  else if(name==='install'){installPrompt?.prompt().catch(()=>{});installPrompt=null;store.state.canInstall=false;}
  else if(name==='install-dismiss'){store.state.installHintDismissed=true;store.persist();}
  else if(name==='transfer-reset')store.state.transferResult=null;
  else if(name==='share')metrics.track('share',{kind:payload});
  else if(name==='import'){
    decodeTransfer(payload).then(toys=>{
      if(!toys)store.state.transferResult={kind:'invalid'};
      else{const added=store.importToys(toys);store.state.transferResult={kind:added?'added':'nothing',count:added};if(added)requestPersistentStorage();}
      render();
    });
    return;
  }
  render();
}
navigation.start();
metrics.track('session');
try{
  stage=new Stage(document.querySelector('#stage'),{
    status,
    seal:()=>{store.seal();render();},
    open:()=>action('open'),
    draw:()=>action('draw'),
    // Wax shells crack with the soft crack; rigid candy and snow shells play their own.
    crack:indices=>{store.crack(indices,stage.recordId);if(!stage.behavior?.rigidShell)toySound.snap();render();},
    touch:kind=>{const id=stage?.recordId;if(id&&!touched.has(id)){touched.add(id);metrics.track('first_touch',{kind});}},
  });
  stage.sync(store.state);
}catch(error){console.error('3D initialization failed',error);status(/WebGL|context/i.test(error.message)?'graphics-error':'app-error');}
document.addEventListener('visibilitychange',()=>{trackPlay();if(document.hidden)metrics.flush();});
// A saved "sound on" can only start audio inside a touch or click.
for(const type of ['pointerdown','pointerup','keydown'])document.addEventListener(type,()=>{if(!store.state.muted&&!toySound.on)toySound.setEnabled(true);},{passive:true});
window.addEventListener('pagehide',()=>{playTimer.update(false,null);metrics.flush();});
window.addEventListener('beforeinstallprompt',event=>{event.preventDefault();installPrompt=event;store.state.canInstall=true;render();});
window.addEventListener('appinstalled',()=>{installPrompt=null;store.state.canInstall=false;metrics.track('install');render();});
// The service worker runs on the public site; during development add ?sw=1.
if('serviceWorker' in navigator&&(window.location.protocol==='https:'||params.has('sw'))){
  window.addEventListener('load',()=>{navigator.serviceWorker.register('/sw.js').catch(error=>console.warn('Service worker registration failed',error));});
}
if(params.has('debug')){
  // Browser QA reads the stage to aim at parts of a toy, and hears which
  // sounds play; only with ?debug=1.
  window.mofuStage=stage;window.mofuSound=toySound;
  const overlay=document.createElement('div');overlay.className='debug-overlay';document.body.append(overlay);
  const canvas=document.querySelector('#stage');
  setInterval(()=>{const d=canvas.dataset;overlay.textContent=`${d.fps??'-'} fps · physics ${d.physicsMs??'-'} ms · render ${d.renderMs??'-'} ms · calls ${d.drawCalls??'-'} · dpr ${d.pixelRatio??'-'} · ${d.quality??''} ${d.kind??''} ${d.rendering??''}`;},500);
}
